import { Link } from '@/components/Link';
import type { SkuBenchmark, SkuUsage } from '../../data/analytics';
import type { Endpoint } from '../../features/catalog/model';
import { Badge } from '../../components/Badge';
import { ProviderAvatars } from '../../components/ProviderAvatars';
import { ProviderArtwork } from '../../components/ProviderArtwork';
import { Sparkline } from '../../components/charts/Sparkline';
import { Icon } from '../../components/Icon';
import { PriceTag } from '../../components/PriceTag';
import { cheapest } from '../../lib/catalog';
import { canChartUsage, fmtSkuReq, hasUsageSignal } from '../../lib/networkUsage';
import { useCatalogLinks } from '../../components/CatalogLinks';
import { EndpointHealth } from './EndpointHealth';
import { m } from '../../paraglide/messages';

export interface EndpointRowProps {
  apiId: string;
  endpoint: Endpoint;
  /** index in the sorted list - drives the "Most used" badge */
  index: number;
  /** per-category color token, e.g. `var(--c-violet)` */
  color: string;
  /** the endpoint's real traffic, when its SKU has any */
  usage?: SkuUsage;
  /** the endpoint's measured health (uptime/latency), when its SKU has any */
  benchmark?: SkuBenchmark;
}

/** Splits a mono path so `:param` segments render in the accent color. */
const MonoPath = ({ path }: { path: string }) => (
  <>
    {path.split(/(:[A-Za-z0-9_]+)/g).map((part, i) =>
      part.startsWith(':') ? (
        <span key={i} className="text-accent">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      ),
    )}
  </>
);

/** One endpoint row: method chip, action, path, desc, usage, health, providers, price. */
export const EndpointRow = ({ apiId, endpoint, index, color, usage, benchmark }: EndpointRowProps) => {
  const links = useCatalogLinks();
  const cheap = cheapest(endpoint);
  const single = endpoint.providers.length === 1;
  // Two separate gates: a count only needs traffic, while a sparkline and a
  // trend arrow additionally need two comparable buckets to point between.
  const hasCount = hasUsageSignal(usage);
  const chartable = canChartUsage(usage);
  const up = chartable && usage.trendPct >= 0;
  return (
    <Link
      to={links.endpoint(apiId, endpoint.id)}
      className="grid cursor-pointer grid-cols-1 items-center gap-x-4.5 gap-y-4 border-b border-rule px-5 py-4 transition-colors last:rounded-b-12 last:border-b-0 hover:bg-wash lg:grid-cols-[minmax(0,1.6fr)_128px_112px_96px_182px_22px]"
    >
      {/* main */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="mono rounded-6 border border-accent-line bg-accent-soft px-1.5 py-px text-10 font-medium tracking-[0.04em] text-accent">
            {endpoint.method}
          </span>
          <h3 className="text-15 font-medium tracking-[-0.012em]">{endpoint.action}</h3>
          {index === 0 && hasCount ? (
            <Badge color={color} className="text-9 tracking-[0.04em]">
              {m.platform_row_most_used()}
            </Badge>
          ) : null}
        </div>
        <div className="mono mt-1 flex min-w-0 flex-wrap items-center gap-2 text-11 text-text-3">
          <span className="min-w-0 truncate">
            <MonoPath path={endpoint.path} />
          </span>
          <span className="max-w-full truncate rounded-4 border border-rule bg-wash px-1.5 py-px text-10 text-text-2">
            {endpoint.sku}
          </span>
        </div>
        <div className="mt-1.5 text-13 text-text-2 [text-wrap:pretty]">
          {endpoint.description}
        </div>
      </div>

      {/* usage - real network traffic; quiet dash while the SKU warms up */}
      <div className="min-w-0">
        <div className="mono text-15 text-text">
          {fmtSkuReq(usage)}
          {hasCount ? <span className="text-10 text-text-3">{m.platform_row_per_month()}</span> : null}
        </div>
        {chartable ? (
          <div className="mt-1.5 flex items-center gap-2">
            <Sparkline
              values={usage.weekly}
              color={up ? 'var(--pos)' : 'var(--text-3)'}
              aria-label={m.platform_row_trend_label({ action: endpoint.action })}
            />
            <span className={`mono text-11 ${up ? 'text-pos' : 'text-text-3'}`}>
              {up ? '▲' : '▼'}
              {Math.abs(usage.trendPct)}%
            </span>
          </div>
        ) : null}
      </div>

      {/* health - measured uptime + p50 through the gateway; dash under the gate */}
      <div className="min-w-0">
        <EndpointHealth benchmark={benchmark} />
      </div>

      {/* providers + price: share a row on mobile, separate grid cells on desktop */}
      <div className="flex items-center gap-2.5 lg:contents">
        <div className="flex flex-1 items-center gap-2.5 lg:flex-none">
          <ProviderAvatars sources={endpoint.providers.map((p) => p.source)} size={22} />
          <span className="text-12 text-text-2">{endpoint.providers.length}</span>
        </div>

        {/* price */}
        <div className="text-right">
          <PriceTag price={cheap.price} label={single ? m.platform_row_price_label() : m.platform_row_from_label()} explain />
          <div className="mt-0.5 flex items-center justify-end gap-1.5 text-11 text-text-2">
            <ProviderArtwork source={cheap.source} size={14} />
            {cheap.source.name}
          </div>
        </div>
      </div>

      {/* chevron */}
      <div className="hidden place-items-center text-text-3 lg:grid">
        <Icon name="chevron-right" size={16} />
      </div>
    </Link>
  );
};
